<script setup>
import { nextTick, ref } from 'vue'
import Button from '@/Components/Buttons/Button.vue'
import Link from '@/Components/Links/Link.vue'
import storage from '@/Utils/storage.js'

defineProps({
  /**
   * The cookies url page
   */
  url: {
    type: String,
    default: '/cookies',
  },
})

const confirmationMessageElement = ref(null)
const alreadyAcknowledged = ref(
  Boolean(storage.get({ type: storage.types.cookie, name: 'cookie-notification-acknowledged' })),
)
const recentlyAcknowledged = ref(false)

const onAcceptAll = () => {
  setAllOptionalPermissions(true)
  focusConfirmation()
}

const onAcceptEssential = () => {
  setAllOptionalPermissions(false)
  focusConfirmation()
}

const focusConfirmation = () => {
  nextTick(() => {
    if (confirmationMessageElement.value) {
      confirmationMessageElement.value.focus()
    }
  })
}

const setAllOptionalPermissions = (allow) => {
  const cookiePermissions = JSON.parse(JSON.stringify(storage.categories))
  for (const key in cookiePermissions) {
    if (key === storage.categories.necessary) {
      cookiePermissions[key] = true
    } else {
      cookiePermissions[key] = allow
    }
  }

  storage.setCookie(storage.categories.necessary, 'cookiePermissions', JSON.stringify(cookiePermissions), 365)
  storage.setCookie(storage.categories.necessary, 'cookie-notification-acknowledged', 'yes', 365)

  recentlyAcknowledged.value = true
}
</script>

<template>
  <div
    v-if="!alreadyAcknowledged && !recentlyAcknowledged"
    data-nosnippet
    role="region"
    aria-label="Cookie Banner"
    id="cookie-banner"
  >
    <div class="container py-6">
      <div
        role="heading"
        class="hidden"
      >
        Information
      </div>
      <div>
        <p class="mb-4">
          We use
          <!-- prettier-ignore -->
          <Link
            class="font-normal"
            as="a"
            target="_blank"
            aria-label="Cookie policy opens in a new tab."
            href="/cookies"
          >cookies</Link>
          to collect anonymous data to help us improve your site browsing experience.
        </p>
        <p>
          Click 'Accept all cookies' to agree to all cookies that collect anonymous data. To only allow the cookies that
          make the site work, click 'Use essential cookies only.' Visit 'Set cookie preferences' to control specific
          cookies.
        </p>
      </div>
      <div
        class="mt-8 text-center *:mb-4 *:w-full md:inline-flex md:flex-wrap md:items-center md:*:mr-4 md:*:mt-0 *:md:w-auto"
      >
        <Button
          type="button"
          @click="onAcceptAll"
        >
          Accept all cookies
        </Button>
        <Button
          variant="secondary-alt"
          type="button"
          @click="onAcceptEssential"
        >
          Use essential cookies only
        </Button>
        <Link
          :href="url"
          class="text-center font-normal"
          text="Set cookies preferences"
        >
          Set cookies preferences
        </Link>
      </div>
    </div>
  </div>

  <div
    v-if="recentlyAcknowledged"
    ref="confirmationMessageElement"
    class="flex bg-state-positive p-2"
    role="alert"
    tabindex="-1"
    id="cookie-confirmation"
  >
    <div class="container">
      <p margin-class="mb-0">Your cookie preferences have been saved.</p>
    </div>
  </div>
</template>
